// Building preview (PORTAL-WORKFLOW.md step 4): one shortlisted building, still anonymous
// (no name, address or exact figure), with indicative outputs as ranges, what the pack
// holds, and the Order button. Ordering holds the building for this company and spends
// credits in one step (POST /v1/buildings/{ref}/order), so nobody else can take it.
//
// As on Find buildings: never say where the data comes from (CLAUDE.md), and (Neil, 11 Oct 2026:
// "make it so that they can't guess too much") no floor area or electricity bands, no overdue or
// older-data flags; "Public building" ("Business premises" when private) with "Sector not shown",
// and payback and grid "Not shown", where the API's anonymity rule says so. The indicative outputs are ranges for the size band.
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Lock, RefreshCw, ShoppingCart } from "lucide-react";
import { ApiError, useApi, type BuildingPreview, type PlacedOrder } from "../api";
import { useBreadcrumbLabel } from "../breadcrumbs";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PurchaseTerms, type TermsAcceptance } from "../components/PurchaseTerms";
import { DistanceBadge, EnergyBadge, GridBadge, OpportunityBadge, PaybackValue, RoofBadge, SECTOR_LABELS, SectorNote, SolarBadge } from "../components/SearchBands";
// SECTOR_LABELS: the sectors' short labels, shared with Find (private commercial adds warehouses, hotels, other)
import "../dashboard.css";
import "../find.css";

/** "9 April 2027" */
const longDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

const plural = (n: number) => `${n.toLocaleString("en-GB")} report credit${n === 1 ? "" : "s"}`;

export function BuildingPage() {
  const api = useApi();
  const { ref = "" } = useParams();
  // Back to the shortlist with its filters, sort and page as they were
  const back = `/find${(useLocation().state as { from?: string } | null)?.from ?? ""}`;

  const [data, setData] = useState<{
    preview: BuildingPreview;
    creditBalance?: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  // After an order or renewal: what happened, and (an order) the property just bought, to link to
  const [done, setDone] = useState<{ text: string; order?: PlacedOrder } | null>(null);
  // What the confirmation is for: a first order, or keeping the building longer
  const [mode, setMode] = useState<"order" | "renew">("order");

  const load = useCallback(() => {
    setError(null);
    return api
      .getBuilding(ref)
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, [api, ref]);

  useEffect(() => {
    load();
  }, [load]);

  // The purchase terms, ticked for this property in the dialog (sent and recorded with the order)
  const [accepted, setAccepted] = useState<TermsAcceptance>(null);
  // Estimated electricity use: acknowledged in the same card as the terms
  const [acceptEstimate, setAcceptEstimate] = useState(false);

  /** Place the order or renewal; on "taken", "renew" or "credits" show why and refresh the preview. */
  const order = async () => {
    setOrdering(true);
    setOrderError(null);
    try {
      if (!accepted) return;
      const ticks = { ...accepted, estimateAccepted: estimated && acceptEstimate };
      const placed = mode === "renew" ? await api.renewBuilding(ref, ticks) : await api.orderBuilding(ref, ticks);
      setDone(
        mode === "renew"
          ? { text: `Renewed. It's exclusive to you until ${longDate(placed.exclusiveUntil)}.` }
          : { text: `Ordered. It's exclusive to you until ${longDate(placed.exclusiveUntil)}, and we'll email you when the report is ready.`, order: placed },
      );
      setConfirming(false);
      await load();
    } catch (e) {
      setConfirming(false);
      setOrderError(e instanceof Error ? e.message : "The order didn't go through. Please try again.");
      if (e instanceof ApiError && (e.status === 409 || e.status === 402)) await load();
    } finally {
      setOrdering(false);
    }
  };

  const p = data?.preview;
  // The breadcrumb names the building type, not the reference in the address
  useBreadcrumbLabel(ref, p?.buildingType ?? (p?.status === "unavailable" ? "Not available" : undefined));
  const balance = data?.creditBalance ?? 0;
  const cost = p?.credits ?? 0;
  const canAfford = balance >= cost;
  const estimated = p?.electricityData === "estimated";

  return (
    <div className="page">
      <Link to={back} className="building-back">
        <ArrowLeft size={16} aria-hidden="true" /> Back to Search properties
      </Link>

      {error && <div className="flash flash-danger">{error}</div>}

      {p?.status === "unavailable" && (
        <div className="card find-empty">
          <h2>Another installer has this property</h2>
          <p className="card-sub">It's exclusive to another installer for now. If their exclusivity ends without renewal, it comes back to your shortlist.</p>
          <Link to={back} className="btn btn-secondary">
            Back to your shortlist
          </Link>
        </div>
      )}

      {p && p.status !== "unavailable" && (
        <>
          <div className="page-head">
            <h1>{p.buildingType}</h1>
            <p>
              {/* The sector's label, or "Sector not shown" on a generic card */}
              <SectorNote sector={p.sector} labels={SECTOR_LABELS} /> · {p.district}
            </p>
          </div>

          {done && (
            <div className="flash flash-success order-done">
              <span>{done.text}</span>
              {done.order && (
                // Straight to the property they've just bought (named now it's theirs)
                <Link className="order-done-link" to={`/properties/${encodeURIComponent(done.order.orderId)}`}>
                  <b>{[done.order.property?.name, done.order.property?.postcode].filter(Boolean).join(", ") || "Your new property"}</b>
                  {done.order.orderNumber ? ` · ${done.order.orderNumber}` : ""} · Open it in My properties →
                </Link>
              )}
            </div>
          )}
          {orderError && <div className="flash flash-danger">{orderError}</div>}

          <div className="building-grid">
            <div className="building-main">
              {/* The shortlist's bands, in full */}
              <section className="card">
                <h2 className="building-h2">The property</h2>
                <dl className="building-facts">
                  <Fact label="Indicative system" value={p.sizeLabel} />
                  <div>
                    <dt>Payback</dt>
                    <dd>
                      <PaybackValue value={p.payback} />
                    </dd>
                  </div>
                  {/* The search bands, as on the Find list (hover: what each means) */}
                  <div>
                    <dt>Opportunity</dt>
                    <dd>
                      <OpportunityBadge value={p.opportunity} />
                    </dd>
                  </div>
                  <div>
                    <dt>Grid</dt>
                    <dd>
                      <GridBadge value={p.grid} />
                    </dd>
                  </div>
                  {/* Four more bands (Neil, 11 Oct 2026); roof and rating (like payback and grid) may be "Not shown" (anonymity) */}
                  <div>
                    <dt>Roof size</dt>
                    <dd>
                      <RoofBadge value={p.roof} />
                    </dd>
                  </div>
                  <div>
                    <dt>Energy rating</dt>
                    <dd>
                      <EnergyBadge value={p.energyRating} />
                    </dd>
                  </div>
                  <div>
                    <dt>Existing solar</dt>
                    <dd>
                      <SolarBadge value={p.solar} />
                    </dd>
                  </div>
                  <div>
                    <dt>Distance</dt>
                    <dd>
                      {p.distance ? (
                        <DistanceBadge value={p.distance} />
                      ) : (
                        // No base postcode yet: say where to set one
                        <Link to="/profile" className="find-base-prompt">
                          Set your base postcode
                        </Link>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Data</dt>
                    <dd>
                      <span className={`find-data find-data-${p.electricityData}`}>{p.electricityData === "actual" ? "Actual data" : "Estimated"}</span>
                    </dd>
                  </div>
                </dl>
              </section>

              {/* Indicative outputs, as ranges */}
              {p.indicative && (
                <section className="card">
                  <h2 className="building-h2">What solar could do here</h2>
                  <dl className="building-facts">
                    <Fact label="Generation" value={p.indicative.generation} />
                    <Fact label="First-year saving" value={p.indicative.yearOneSaving} />
                    <Fact label="Carbon saved" value={p.indicative.co2} />
                  </dl>
                  <p className="find-note muted">
                    Indicative ranges for a system of this size ({p.sizeLabel}), not this property's own figures. The pack replaces them with a roof scan and
                    a full costing.
                  </p>
                </section>
              )}
            </div>

            {/* Order card: what the pack holds, the cost, the button */}
            <aside className="card building-order">
              {p.status === "yours" || p.status === "lapsed" ? (
                <>
                  <h2 className="building-h2">
                    <CheckCircle2 size={18} aria-hidden="true" className="building-ok" />{" "}
                    {p.status === "yours" ? "This property is yours" : "Your exclusivity has ended"}
                  </h2>
                  <p className="card-sub">
                    {p.status === "yours"
                      ? `No other installer can see or order it until ${longDate(p.exclusivity?.until)}.`
                      : `It ended on ${longDate(p.exclusivity?.until)}. Nobody else has taken it yet, so you can renew it to keep it.`}
                  </p>
                  {p.exclusivity?.canRenew ? (
                    <>
                      <div className="building-cost">
                        <span>Renew for 6 months</span>
                        <b>{plural(cost)}</b>
                      </div>
                      <div className="building-cost muted">
                        <span>Your balance</span>
                        <span>{plural(balance)}</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary building-cta"
                        disabled={!canAfford || ordering}
                        onClick={() => {
                          setMode("renew");
                          setAcceptEstimate(false);
                          setConfirming(true);
                        }}
                      >
                        <RefreshCw size={16} aria-hidden="true" /> Renew
                      </button>
                      {!canAfford && <p className="building-small muted">You don't have enough report credits to renew yet.</p>}
                    </>
                  ) : (
                    <p className="building-small muted">You can renew it from 30 days before it ends.</p>
                  )}
                </>
              ) : (
                <>
                  <h2 className="building-h2">The pack</h2>
                  <ul className="building-pack">
                    {(p.packContents ?? []).map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                  <div className="building-cost">
                    <span>Cost</span>
                    <b>{plural(cost)}</b>
                  </div>
                  <div className="building-cost muted">
                    <span>Your balance</span>
                    <span>{plural(balance)}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary building-cta"
                    disabled={!canAfford || ordering}
                    onClick={() => {
                      setMode("order");
                      setAcceptEstimate(false);
                      setConfirming(true);
                    }}
                  >
                    <ShoppingCart size={16} aria-hidden="true" /> Order report
                  </button>
                  <p className="building-small muted">
                    <Lock size={12} aria-hidden="true" />{" "}
                    {canAfford
                      ? "Ordering makes it exclusive to you for 6 months: no other installer can see or order it."
                      : "You don't have enough report credits for this report yet."}
                  </p>
                </>
              )}
            </aside>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirming}
        title={mode === "renew" ? "Renew for 6 months?" : "Order this report?"}
        confirmLabel={`${mode === "renew" ? "Renew" : "Order"} for ${plural(cost)}`}
        danger={false}
        size="large"
        busy={ordering}
        confirmDisabled={!accepted || (estimated && !acceptEstimate)}
        onConfirm={order}
        onCancel={() => setConfirming(false)}
      >
        <p>
          {mode === "renew"
            ? `${plural(cost)} will come off your balance (${plural(balance)} now) and the property stays yours for another 6 months.`
            : `${plural(cost)} will come off your balance (${plural(balance)} now) and the property is exclusive to your company for 6 months from today. We'll email you when the report is ready.`}
        </p>
        {confirming && (
          <PurchaseTerms
            credits={cost}
            onChange={setAccepted}
            extra={
              estimated
                ? {
                    label: "I understand this report uses estimated electricity use, not the property's actual figures.",
                    checked: acceptEstimate,
                    onChange: setAcceptEstimate,
                  }
                : undefined
            }
          />
        )}
      </ConfirmDialog>
    </div>
  );
}

/** One label and value in a facts list; a dash when there's no value. */
function Fact({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value ?? "—"}</dd>
    </div>
  );
}
